import { useCallback, useRef, useEffect, useContext } from "react";
import { doc, updateDoc, arrayUnion } from "firebase/firestore";
import UserContext from "../assets/user_context";
import { useAuth } from "../services/use-auth";

/**
 * Decoupled analytics hook for onboarding tour.
 * Writes analytics data to users/{uid}.tourAnalytics in Firestore.
 * Uses arrayUnion for step tracking to prevent overwrites on rapid transitions.
 */
export function useTourAnalytics() {
  const { user, orgData } = useContext(UserContext);
  const { db } = useAuth();

  const userRef = useRef(user);
  const dbRef = useRef(db);
  const orgDataRef = useRef(orgData);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { dbRef.current = db; }, [db]);
  useEffect(() => { orgDataRef.current = orgData; }, [orgData]);

  // W3 §4.11 sweep: trial orgs are excluded from product analytics.
  const skipForTrial = () => orgDataRef.current?.trial === true;

  const trackStarted = useCallback(async () => {
    const currentUser = userRef.current;
    const currentDb = dbRef.current;
    if (!currentUser?.uid || !currentDb || skipForTrial()) return;
    try {
      await updateDoc(doc(currentDb, "users", currentUser.uid), {
        'tourAnalytics.startedAt': new Date().toISOString(),
      });
    } catch (err) {
      console.warn("Tour analytics: failed to track started", err);
    }
  }, []);

  const trackCompleted = useCallback(async () => {
    const currentUser = userRef.current;
    const currentDb = dbRef.current;
    if (!currentUser?.uid || !currentDb || skipForTrial()) return;
    try {
      await updateDoc(doc(currentDb, "users", currentUser.uid), {
        'tourAnalytics.completedAt': new Date().toISOString(),
      });
    } catch (err) {
      console.warn("Tour analytics: failed to track completed", err);
    }
  }, []);

  const trackDismissed = useCallback(async (stepId, stepIndex, orgId) => {
    const currentUser = userRef.current;
    const currentDb = dbRef.current;
    if (!currentUser?.uid || !currentDb || skipForTrial()) return;
    try {
      await updateDoc(doc(currentDb, "users", currentUser.uid), {
        'tourAnalytics.skippedAt': new Date().toISOString(),
        'tourAnalytics.skippedAtStep': stepId,
        'tourAnalytics.skippedAtStepIndex': stepIndex,
        'tourAnalytics.orgId': orgId || null,
      });
    } catch (err) {
      console.warn("Tour analytics: failed to track dismissed", err);
    }
  }, []);

  const trackStepViewed = useCallback(async (stepId, stepIndex) => {
    const currentUser = userRef.current;
    const currentDb = dbRef.current;
    if (!currentUser?.uid || !currentDb || skipForTrial()) return;
    try {
      await updateDoc(doc(currentDb, "users", currentUser.uid), {
        'tourAnalytics.stepsViewed': arrayUnion({
          stepId,
          stepIndex,
          timestamp: new Date().toISOString(),
        }),
      });
    } catch (err) {
      console.warn("Tour analytics: failed to track step viewed", err);
    }
  }, []);

  // --- Page-tour analytics (guided-navigation W1.7) -----------------------
  // Keyed by tourId under tourAnalytics.pageTours so many page tours coexist
  // on the same user doc without disturbing the onboarding-tour fields above.
  const trackPageTourStarted = useCallback(async (tourId, launchedFrom = "hud") => {
    const currentUser = userRef.current;
    const currentDb = dbRef.current;
    if (!currentUser?.uid || !currentDb || !tourId || skipForTrial()) return;
    try {
      await updateDoc(doc(currentDb, "users", currentUser.uid), {
        [`tourAnalytics.pageTours.${tourId}.startedAt`]: new Date().toISOString(),
        [`tourAnalytics.pageTours.${tourId}.launchedFrom`]: launchedFrom,
      });
    } catch (err) {
      console.warn("Tour analytics: failed to track page tour started", err);
    }
  }, []);

  const trackPageTourCompleted = useCallback(async (tourId) => {
    const currentUser = userRef.current;
    const currentDb = dbRef.current;
    if (!currentUser?.uid || !currentDb || !tourId || skipForTrial()) return;
    try {
      await updateDoc(doc(currentDb, "users", currentUser.uid), {
        [`tourAnalytics.pageTours.${tourId}.completedAt`]: new Date().toISOString(),
      });
    } catch (err) {
      console.warn("Tour analytics: failed to track page tour completed", err);
    }
  }, []);

  return {
    trackStarted,
    trackCompleted,
    trackDismissed,
    trackStepViewed,
    trackPageTourStarted,
    trackPageTourCompleted,
  };
}
